import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { SpotifyBioUploadResponse } from 'fym-shared/src/music24/artistProfile';
import { useUploadBioToSpotifyMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { reportStudioActionError } from '../../studio/studioActionErrorText';
import { spotifyBioPostOutcome, type SpotifyBioPostOutcome } from './spotifyBioState';
import { spotifyBioMessages as s } from './spotifyBioMessages';

/** The sentence of a refused Send, or null when the sheet changes its view instead. */
function outcomeText(intl: ReturnType<typeof useIntl>, outcome: SpotifyBioPostOutcome, artist: string): string | null {
  switch (outcome.kind) {
    case 'rights':
      return intl.formatMessage(s.rightsRequired);
    case 'nothing':
      return intl.formatMessage(s.nothing);
    case 'tooLong':
      return intl.formatMessage(s.tooLong);
    case 'noPermission':
      return intl.formatMessage(s.noPermission, { artist });
    case 'refused':
      return outcome.detail === null
        ? intl.formatMessage(s.refusedNoDetail)
        : intl.formatMessage(s.refused, { detail: outcome.detail });
    case 'failed':
      return intl.formatMessage(s.failed);
    case 'generic':
      return intl.formatMessage(s.generic);
    case 'manual':
    case 'notConnected':
      return null;
  }
}

/**
 * Send of the bio sheet (`POST .../artist-profile/spotify/bio { acceptRights? }`). `acceptRights`
 * goes only when the artist ticked the line. A refusal gives a sentence, or a view change: no Auto
 * upload access shows the steps, no sign-in reads the profile again (Connect).
 */
export function useSpotifyBioUpload(accountId: number, artist: string, onReload: () => void) {
  const intl = useIntl();
  const [post, { isLoading }] = useUploadBioToSpotifyMutation();
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState(false);
  const [done, setDone] = useState<SpotifyBioUploadResponse | null>(null);
  const send = useCallback(
    (acceptRights: boolean) => {
      setError(null);
      setDone(null);
      post({ accountId, acceptRights: acceptRights ? true : undefined })
        .then((result) => {
          if (isSuccessfulResult(result)) {
            setDone(result.data);
            return;
          }
          const outcome = spotifyBioPostOutcome(result.error);
          if (outcome.kind === 'generic') {
            reportStudioActionError('profile spotify bio upload', result.error);
          }
          if (outcome.kind === 'manual') {
            setManual(true);
          }
          if (outcome.kind === 'notConnected' || outcome.kind === 'nothing') {
            onReload();
          }
          setError(outcomeText(intl, outcome, artist));
        })
        .catch(console.error);
    },
    [accountId, artist, intl, onReload, post]
  );
  const reset = useCallback(() => {
    setError(null);
    setDone(null);
  }, []);
  return { send, isSending: isLoading, error, manual, done, reset };
}
